<template>
  <div class="notification-list">
    <!-- Пусто, новая точка (p7b): колокольчик и подпись, без счётчика и «Прочитать все» -->
    <div v-if="!notifications.length" class="notification-list__empty">
      <img
        :src="emptyImage"
        :srcset="`${emptyImage2x} 2x`"
        width="160"
        height="160"
        alt=""
      >
      <div class="notification-list__empty-title">
        Список уведомлений пуст
      </div>
      <div class="notification-list__empty-text">
        Здесь появятся ответы поддержки по обращениям филиала
      </div>
    </div>

    <template v-else>
      <div class="notification-list__header">
        <span class="notification-list__title">Уведомления</span>
        <span v-if="unreadSupport" class="notification-list__counter">{{ unreadSupport }}</span>
        <v-spacer />
        <!-- «Прочитать все» гасит и «Решено · оцените помощь» (handoff 08, solved_rate) -->
        <v-btn
          v-if="unreadSupport"
          depressed
          small
          class="notification-list__read-all text-normalize"
          :loading="reading"
          @click="$emit('read', null)"
        >
          <v-icon left size="18">
            mdi-check-all
          </v-icon>
          Прочитать все
        </v-btn>
      </div>

      <div v-for="group in groups" :key="group.label" class="notification-list__group">
        <div class="notification-list__day">
          {{ group.label }}
        </div>

        <template v-for="notification in group.items">
          <notification-support-row
            v-if="isSupport(notification.type)"
            :key="notification.id"
            :notification="notification"
            class="notification-list__row"
            @open="$emit('open-support', $event)"
            @read="$emit('read', [$event.id])"
          />
          <!-- Прочие типы CRM (фискализация, смена, отмена заказа) - как раньше -->
          <v-list-item
            v-else
            :key="notification.id"
            link
            class="notification-list__row notification-list__legacy"
            @click="goToRouteByType(notification)"
          >
            <v-list-item-icon>
              <v-icon
                :color="getNotificationColor(notification.level)"
                v-text="getNotificationIcon(notification.level)"
              ></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title style="font-size: 16px">
                {{ notification.title }}
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="notification.description"
                style="font-size: 14px"
              >
                {{ notification.description }}
              </v-list-item-subtitle>
              <v-list-item-action-text
                class="mt-2"
                style="font-size: 12px"
                v-text="formatDate(notification.createdAt)"
              />
            </v-list-item-content>
            <v-list-item-action v-if="showAction(notification.type)">
              <component :is="getAction(notification.type)" />
            </v-list-item-action>
          </v-list-item>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  import NotificationLevel from '@/models/enums/notification/NotificationLevel'
  import NotificationType from '@/models/enums/notification/NotificationType'
  import { formatIsoDateToCurrentLocaleString } from '@/util/date-time-helper'
  import { groupByDay } from '@/components/support/notifications'
  import NotificationSupportRow from '@/components/support/NotificationSupportRow.vue'
  import emptyImage from '@/assets/support/empty-notifications.png'
  import emptyImage2x from '@/assets/support/empty-notifications@2x.png'
  import CloseShiftAction from './actions/CloseShiftAction.vue'

  export default {
    name: 'NotificationList',
    components: { CloseShiftAction, NotificationSupportRow },
    props: {
      notifications: Array,
      loading: {
        type: Boolean,
        default: false,
      },
      // Запрос «Прочитано» / «Прочитать все» в пути: кнопка крутится, второй клик не уходит.
      reading: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return { emptyImage, emptyImage2x }
    },
    computed: {
      groups () {
        return groupByDay(this.notifications)
      },
      // Счётчик в шапке - только непрочитанные уведомления поддержки: у прочих типов CRM
      // прочитанности на сервере нет, они гаснут при открытии выпадайки.
      unreadSupport () {
        return this.notifications.filter(item => this.isSupport(item.type) && !item.isRead).length
      },
    },
    methods: {
      isSupport (type) {
        return NotificationType.isSupport(type)
      },
      formatDate (date) {
        return formatIsoDateToCurrentLocaleString(date)
      },
      getNotificationIcon (level) {
        switch (level) {
          case NotificationLevel.Warning:
            return 'mdi-alert'
          case NotificationLevel.Error:
            return 'mdi-close'
          default:
            return 'mdi-information'
        }
      },
      getNotificationColor (level) {
        switch (level) {
          case NotificationLevel.Warning:
            return 'warning'
          case NotificationLevel.Error:
            return 'error'
          default:
            return 'grey'
        }
      },
      showAction (type) {
        return type === NotificationType.WorkShiftNotClosed
      },
      getAction (type) {
        if (type === NotificationType.WorkShiftNotClosed) {
          return CloseShiftAction
        }

        return null
      },
      goToRouteByType (notification) {
        if (
          notification.type === NotificationType.WorkShiftNotClosed ||
          notification.type === NotificationType.FiscalCheckPrintFailed ||
          notification.type === NotificationType.PrintCheckFailed
        ) {
          this.goToRoute(
            this.$router.resolve({
              name: 'cashier',
              query: { tab: 'fiscalization' },
            }).route,
          )
        }

        if (notification.type === NotificationType.OrderCanceled) {
          this.goToRoute(
            this.$router.resolve({
              name: 'fastOrderEdit',
              query: { id: notification.orderId },
            }).route,
          )
        }
      },
      goToRoute (route) {
        if (this.$route.fullPath !== route.fullPath) {
          this.$router.push(route)
        }
      },
    },
  }
</script>

<style lang="less" scoped>
@import '../support/tokens.less';

.notification-list {
  padding: 16px;
  background: @fakt-white;
}

.notification-list__header {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 0 4px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid @fakt-divider;
}

.notification-list__title {
  font-size: 16px;
  font-weight: 500;
  color: @fakt-text;
}

// Все бейджи у пользователя розовые (11-acceptance.md)
.notification-list__counter {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  color: @fakt-white;
  background: @fakt-pink;
}

.notification-list__read-all {
  color: @fakt-text !important;
  background-color: @fakt-surface !important;
}

.notification-list__day {
  padding: 12px 4px 8px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: @fakt-text-2;
}

.notification-list__row + .notification-list__row {
  margin-top: 8px;
}

.notification-list__legacy {
  border: 1px solid @fakt-divider;
  border-radius: 8px;
}

.notification-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 40px;
  text-align: center;
}

.notification-list__empty-title {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: @fakt-text;
}

.notification-list__empty-text {
  margin-top: 6px;
  font-size: 14px;
  color: @fakt-text-2;
}
</style>
